import {
  DEFAULT_LOCALE,
  SHORT_CODE_TO_LOCALE,
  SUPPORTED_LOCALES,
} from './supported-locales';

/**
 * Normalizes a locale string to standard format (xx-YY or xx-Xxxx-YY).
 * Handles underscores and varying casing.
 */
export function normalizeLocale(locale: string): string {
  if (!locale) {
    return DEFAULT_LOCALE;
  }

  const parts = locale.replace(/_/g, '-').split('-');
  const language = parts[0].toLowerCase();

  if (parts.length === 1) {
    return language;
  }

  if (parts.length === 2) {
    // Could be language-region (en-US) or language-script (zh-Hans)
    const second = parts[1];
    if (second.length === 2) {
      // Region code
      return `${language}-${second.toUpperCase()}`;
    } else {
      // Script code (e.g., Hans, Latn)
      return `${language}-${second.charAt(0).toUpperCase()}${second.slice(1).toLowerCase()}`;
    }
  }

  if (parts.length === 3) {
    // language-script-region (e.g., sr-Latn-BA)
    const script = parts[1];
    const region = parts[2];
    return `${language}-${script.charAt(0).toUpperCase()}${script.slice(1).toLowerCase()}-${region.toUpperCase()}`;
  }

  return locale;
}

/**
 * Attempts to expand a short locale code using Intl.Locale.maximize().
 * Returns null if Intl.Locale is not available or fails.
 */
export function maximizeLocale(shortCode: string): string | null {
  try {
    // Intl.Locale may not be available in all environments
    if (typeof Intl !== 'undefined' && 'Locale' in Intl) {
      const LocaleConstructor = (Intl as typeof Intl & { Locale: new (tag: string) => Intl.Locale }).Locale;
      const locale = new LocaleConstructor(shortCode);
      const maximized = locale.maximize();

      if (maximized.language && maximized.region) {
        return `${maximized.language}-${maximized.region}`;
      }
    }
  } catch {
    // Intl.Locale failed, return null
  }
  return null;
}

/**
 * Maps a browser/navigator language code to a supported translation locale.
 *
 * Algorithm:
 * 1. Normalize the input locale string
 * 2. If exact match in SUPPORTED_LOCALES, return it
 * 3. If short code (2 letters), try Intl.Locale.maximize() to get full locale
 * 4. If maximized locale is supported, return it
 * 5. If short code has explicit mapping, return mapped value
 * 6. Extract language from full locale and try mapping
 * 7. Return DEFAULT_LOCALE as fallback
 *
 * @param navigatorLanguage - The language code from navigator.language or similar
 * @returns A supported locale code for translations
 */
export function mapToSupportedLocale(navigatorLanguage: string | undefined | null): string {
  if (!navigatorLanguage) {
    return DEFAULT_LOCALE;
  }

  const normalized = normalizeLocale(navigatorLanguage);

  // Direct match with supported locales
  if (SUPPORTED_LOCALES.has(normalized)) {
    return normalized;
  }

  const language = normalized.split('-')[0];

  // Short code (2 letters only)
  if (normalized.length === 2) {
    // Try to maximize using Intl.Locale
    const maximized = maximizeLocale(normalized);
    if (maximized && SUPPORTED_LOCALES.has(maximized)) {
      return maximized;
    }

    // Fall back to explicit short code mapping
    return SHORT_CODE_TO_LOCALE[language] ?? DEFAULT_LOCALE;
  }

  // Full locale not directly supported - try the language's default mapping
  if (SHORT_CODE_TO_LOCALE[language]) {
    return SHORT_CODE_TO_LOCALE[language];
  }

  return DEFAULT_LOCALE;
}
